import { useEffect, useSyncExternalStore } from 'react';

/**
 * React hook for fetching and mutating the authenticated buyer's saved
 * searches. Reads through the product-shaped `customerAccount.buying.savedSearches`
 * API; customer identity is resolved server-side from the session cookie.
 *
 * Cosmos uses `graphql-request` via the generated SDK exposed by
 * `getAthenaClient()`, so this hook composes a small module-level store +
 * `useSyncExternalStore` instead of Apollo `useQuery`/`useMutation`. The store
 * is shared by every consumer (the switcher dropdown, the save button, the
 * rename/delete dialogs), so a create/update/delete from any surface refetches
 * once and all mounted consumers re-render — no prop-drilling or context, and
 * no stale per-instance copies that only refresh on reload.
 */
import { useAuth } from '@opendoor/auth-fe/context';
import type { SavedSearchFieldsFragment, SavedSearchInput } from '__generated__/athena/graphql';
import {
  AddSavedSearchDocument,
  GetSavedSearchesDocument,
  RemoveSavedSearchDocument,
  UpdateSavedSearchDocument,
} from '__generated__/athena/graphql';
import { captureError } from 'helpers/captureError';

import { getAthenaClient } from '../../api';

type SavedSearchMutationResult = {
  success: boolean;
  errorMessage?: string | null;
  savedSearch?: SavedSearchFieldsFragment | null;
};

export type RemoveSavedSearchResult = {
  success: boolean;
  errorMessage?: string | null;
};

type SavedSearchesFetchResult = {
  savedSearches: SavedSearchFieldsFragment[];
  hasLoaded: boolean;
};

interface UseSavedSearchesApi {
  isLoggedIn: boolean;
  loading: boolean;
  /**
   * True once a fetch for the current session has completed successfully
   * (including an empty result). Failures leave this false so consumers do not
   * treat an unknown list as authoritative — e.g. the page only clears its
   * active saved search when the loaded list genuinely no longer contains it.
   */
  hasLoaded: boolean;
  savedSearches: SavedSearchFieldsFragment[];
  add: (input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  update: (id: string, input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  remove: (id: string) => Promise<RemoveSavedSearchResult>;
  /**
   * Resolves with the current store list and whether a successful load has
   * completed for this session. Callers that must not treat an unknown list as
   * empty (e.g. duplicate detection) should gate on `hasLoaded`.
   */
  refetch: () => Promise<SavedSearchesFetchResult>;
}

// The Athena resolver rejects saved searches without any location (market,
// zone, or polygon) with `success: false`. Catch that here so the hook
// surfaces a uniform client-side error without paying for a round-trip.
const inputHasLocation = (input: SavedSearchInput): boolean =>
  !!input.location.market ||
  (input.location.zoneIds?.length ?? 0) > 0 ||
  (input.location.vertices?.length ?? 0) > 0;

const LOCATION_REQUIRED_ERROR = 'A location is required to save a search.';

// ---- Shared module-level store ----
// Saved searches are read and mutated from several surfaces at once. A single
// module store keeps them in sync via `useSyncExternalStore`: mutations refetch
// once and every subscribed consumer re-renders.

interface SavedSearchesStoreState {
  savedSearches: SavedSearchFieldsFragment[];
  loading: boolean;
  hasLoaded: boolean;
}

let storeState: SavedSearchesStoreState = {
  savedSearches: [],
  loading: false,
  hasLoaded: false,
};
const listeners = new Set<() => void>();
// Latest in-flight request, so a stale response from a previous auth state
// (e.g. logout-mid-fetch) cannot overwrite fresher state.
let requestId = 0;
// Latest auth state, so a mutation that resolves after logout doesn't refetch
// against an unauthenticated session.
let loggedIn = false;

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): SavedSearchesStoreState => storeState;

const setStoreState = (next: Partial<SavedSearchesStoreState>) => {
  storeState = { ...storeState, ...next };
  listeners.forEach((listener) => listener());
};

// Several surfaces (switcher, save button, page controller) mount this hook
// and their login effects all fire in the same commit. Collapse those
// concurrent loads into one request; mutations bypass this via `forceFetch`.
let inFlightFetch: Promise<SavedSearchesFetchResult> | null = null;

const currentFetchResult = (): SavedSearchesFetchResult => ({
  savedSearches: storeState.savedSearches,
  hasLoaded: storeState.hasLoaded,
});

const fetchSavedSearches = (): Promise<SavedSearchesFetchResult> => {
  if (inFlightFetch) {
    return inFlightFetch;
  }
  const promise: Promise<SavedSearchesFetchResult> = doFetchSavedSearches().finally(() => {
    if (inFlightFetch === promise) {
      inFlightFetch = null;
    }
  });
  inFlightFetch = promise;
  return inFlightFetch;
};

// A fetch that must observe a just-committed mutation cannot join an in-flight
// request that started before the mutation.
const forceFetchSavedSearches = (): Promise<SavedSearchesFetchResult> => {
  inFlightFetch = null;
  return fetchSavedSearches();
};

const doFetchSavedSearches = async (): Promise<SavedSearchesFetchResult> => {
  const id = ++requestId;
  setStoreState({ loading: true });
  try {
    const data = await getAthenaClient().request(GetSavedSearchesDocument);
    if (id !== requestId) {
      return currentFetchResult();
    }
    // Preserve Athena/buyer-service order. `createdAt` is currently stubbed to
    // epoch in `fromGrpcSavedSearch` until the buyer proto exposes a real
    // timestamp, so a client-side recency sort would be a no-op on real data.
    const savedSearches = data?.customerAccount?.buying?.savedSearches ?? [];
    setStoreState({
      savedSearches,
      hasLoaded: true,
    });
    return { savedSearches, hasLoaded: true };
  } catch (e) {
    captureError(e, 'GetSavedSearches');
    if (id !== requestId) {
      return currentFetchResult();
    }
    // Keep hasLoaded false on first-load failure so consumers do not treat an
    // unknown list as authoritative (e.g. clearing ssid, skipping duplicate
    // checks). Preserve any last-known-good savedSearches already in the store.
    return currentFetchResult();
  } finally {
    if (id === requestId) {
      setStoreState({ loading: false });
    }
  }
};

const clearSavedSearches = () => {
  requestId++;
  // Drop any in-flight join target: its state writes are already suppressed by
  // the requestId bump, so a later login must start a fresh request rather
  // than await a fetch whose results will be discarded.
  inFlightFetch = null;
  setStoreState({ savedSearches: [], loading: false, hasLoaded: false });
};

// Apply a successful mutation to the module store before the reconciliation
// refetch. If that refetch fails, consumers still see the committed record
// (so active-ssid reconciliation does not clear a just-created id).
const upsertSavedSearchInStore = (savedSearch: SavedSearchFieldsFragment) => {
  const existing = storeState.savedSearches;
  const idx = existing.findIndex((item) => item.id === savedSearch.id);
  if (idx === -1) {
    setStoreState({
      savedSearches: [...existing, savedSearch],
      hasLoaded: true,
    });
    return;
  }
  const next = existing.slice();
  next[idx] = savedSearch;
  setStoreState({ savedSearches: next, hasLoaded: true });
};

const removeSavedSearchFromStore = (id: string) => {
  setStoreState({
    savedSearches: storeState.savedSearches.filter((item) => item.id !== id),
  });
};

const addSavedSearch = async (input: SavedSearchInput): Promise<SavedSearchMutationResult> => {
  if (!inputHasLocation(input)) {
    return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
  }
  try {
    const res = await getAthenaClient().request(AddSavedSearchDocument, { input });
    const payload = res?.customerAccount?.buying?.savedSearches?.add;
    if (payload?.success && loggedIn) {
      if (payload.savedSearch) {
        upsertSavedSearchInStore(payload.savedSearch);
      }
      await forceFetchSavedSearches();
    }
    return {
      success: payload?.success ?? false,
      errorMessage: payload?.errorMessage ?? null,
      savedSearch: payload?.savedSearch ?? null,
    };
  } catch (e) {
    captureError(e, 'AddSavedSearch');
    return { success: false, errorMessage: 'Unexpected error', savedSearch: null };
  }
};

const updateSavedSearch = async (
  id: string,
  input: SavedSearchInput,
): Promise<SavedSearchMutationResult> => {
  if (!inputHasLocation(input)) {
    return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
  }
  try {
    const res = await getAthenaClient().request(UpdateSavedSearchDocument, { id, input });
    const payload = res?.customerAccount?.buying?.savedSearches?.update;
    if (payload?.success && loggedIn) {
      if (payload.savedSearch) {
        upsertSavedSearchInStore(payload.savedSearch);
      }
      await forceFetchSavedSearches();
    }
    return {
      success: payload?.success ?? false,
      errorMessage: payload?.errorMessage ?? null,
      savedSearch: payload?.savedSearch ?? null,
    };
  } catch (e) {
    captureError(e, 'UpdateSavedSearch');
    return { success: false, errorMessage: 'Unexpected error', savedSearch: null };
  }
};

const removeSavedSearch = async (id: string): Promise<RemoveSavedSearchResult> => {
  try {
    const res = await getAthenaClient().request(RemoveSavedSearchDocument, { id });
    const payload = res?.customerAccount?.buying?.savedSearches?.remove;
    if (payload?.success && loggedIn) {
      removeSavedSearchFromStore(id);
      await forceFetchSavedSearches();
    }
    return {
      success: payload?.success ?? false,
      errorMessage: payload?.errorMessage ?? null,
    };
  } catch (e) {
    captureError(e, 'RemoveSavedSearch');
    return { success: false, errorMessage: 'Unexpected error' };
  }
};

/**
 * Resets the module-level store. Exported for tests (and HMR) to isolate state
 * between runs; not used by application code.
 */
export const __resetSavedSearchesStoreForTest = () => {
  storeState = { savedSearches: [], loading: false, hasLoaded: false };
  requestId += 1;
  loggedIn = false;
  inFlightFetch = null;
};

export function useSavedSearches(): UseSavedSearchesApi {
  const { authentication } = useAuth();
  const authState = authentication?.state;
  const isLoggedIn = authState === 'authenticated';
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    loggedIn = isLoggedIn;
    if (isLoggedIn) {
      void fetchSavedSearches();
      return;
    }
    // `authenticating` is the transient state emitted on initial load and
    // session refresh. Clearing the list (and flipping the dropdown's
    // visibility guard) during it would let a background re-render tear down
    // an open saved-searches menu, so we keep the existing list and only reset
    // on a definitively logged-out state.
    if (authState === 'authenticating') {
      return;
    }
    clearSavedSearches();
  }, [isLoggedIn, authState]);

  return {
    isLoggedIn,
    loading: snapshot.loading,
    hasLoaded: snapshot.hasLoaded,
    savedSearches: snapshot.savedSearches,
    add: addSavedSearch,
    update: updateSavedSearch,
    remove: removeSavedSearch,
    refetch: fetchSavedSearches,
  };
}
